import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { nextSubmission, startSubmission } from '../../../service';
import { getUserRole } from '../../../userLocalDetails';
import {
  getLaunchContext,
  clearLaunchContext,
} from '../../../utils/ltiEmbedded';
import {
  IbmplexsansMediumWhite16px,
  IbmplexsansNormalShark16px,
} from '../../../styledMixins';
import { FeedbackPageSkeleton } from '../../Skeleton';

export default function TaskDetail() {
  const { assignmentId } = useParams();
  const history = useHistory();
  // A failed start left this route mounted (history.push('#') goes nowhere), so
  // the skeleton would spin forever. postApi resolves null on an error it has
  // already toasted; undefined is a 401 that service.js hands to the session
  // flow (redirect or LMS relaunch notice), so that one keeps the skeleton.
  const [startFailed, setStartFailed] = useState(false);

  useEffect(() => {
    // A start that resolves after the viewer has left this page (or moved to
    // another task) must not pull them back to its submission or flip its
    // state: only the current start may act.
    let active = true;
    setStartFailed(false);
    const role = getUserRole();

    if (role === 'TEACHER') {
      nextSubmission(assignmentId)
        .then((res) => {
          if (!active) return;
          if (res !== undefined && res !== null) {
            history.push(`/submissions/${res}`);
          } else {
            history.push(`/tasks/${assignmentId}`);
          }
        })
        .catch((error) => {
          console.error(error);
          if (active) setStartFailed(true);
        });
    } else {
      const lctx = getLaunchContext();
      startSubmission(lctx ? { assignmentId, lctx } : { assignmentId })
        .then((res) => {
          if (!active || res === undefined) return;
          // A start that names no submission did not open one: say so rather
          // than open /submissions/undefined.
          if (res !== null && res.id != null && res.id !== '') {
            history.push(`/submissions/${res.id}`);
          } else {
            setStartFailed(true);
          }
        })
        .catch((error) => {
          console.error(error);
          if (active) setStartFailed(true);
        })
        .finally(() => {
          // Clear the one-time launch context regardless of outcome so a later
          // non-LTI submission of a different assignment can't reuse a stale
          // context after a failed start. No-op when nothing is stored.
          clearLaunchContext();
        });
    }
    return () => {
      active = false;
    };
  }, [assignmentId, history]);

  if (startFailed) {
    // No retry: the launch context is already cleared, so a second POST would
    // lose an embedded student's LMS link. /tasks/:id would only redirect back
    // here for a student, so the way out is the task list.
    return (
      <StartFailed role="alert">
        <StartFailedText>
          We couldn&apos;t open this task. Please try again in a moment.
        </StartFailedText>
        <BackToTasksButton type="button" onClick={() => history.push('/tasks')}>
          Back to tasks
        </BackToTasksButton>
      </StartFailed>
    );
  }

  return <FeedbackPageSkeleton />;
}

const StartFailed = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px 16px;
  text-align: center;
`;

const StartFailedText = styled.p`
  ${IbmplexsansNormalShark16px}
  margin: 0;
`;

const BackToTasksButton = styled.button`
  ${IbmplexsansMediumWhite16px}
  background-color: var(--light-mode-purple);
  padding: 8px 16px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
`;
